import isCaptureInProgress from '@Application/ApplicationSettings/CaptureActivity/isCaptureInProgress';
import shareCaptureActivity from '@Application/ApplicationSettings/CaptureActivity/shareCaptureActivity';
import { setCaptureInProgress } from '@Guidelines/Shell/Signals/captureInProgressSignal';
import useCapturePort from '@UI/Providers/PortsProvider/Hooks/Capture/useCapturePort';
import * as React from 'react';

type CaptureActivitySharing = ReturnType<typeof shareCaptureActivity>;

/**
 * Renders nothing: publishes on `captureInProgressSignal` whether a capture runs in this tab or in
 * another one, and announces this tab's activity to the others.
 */
const CaptureActivityHost: React.FC = () => {
	const capturePort = useCapturePort();
	const [isActiveHere, setIsActiveHere] = React.useState<boolean>(() =>
		isCaptureInProgress(capturePort.snapshot().state),
	);
	const [isActiveElsewhere, setIsActiveElsewhere] = React.useState<boolean>(false);
	const sharingRef = React.useRef<CaptureActivitySharing | null>(null);

	React.useEffect(() => {
		setIsActiveHere(isCaptureInProgress(capturePort.snapshot().state));

		return capturePort.subscribe((snapshot) => {
			setIsActiveHere(isCaptureInProgress(snapshot.state));
		});
	}, [capturePort]);

	// Created before the announce effect so that effect always finds the sharing in the ref.
	React.useEffect(() => {
		const sharing = shareCaptureActivity({ onElsewhereActivityChange: setIsActiveElsewhere });
		sharingRef.current = sharing;

		return () => {
			sharing.dispose();
			sharingRef.current = null;
		};
	}, []);

	React.useEffect(() => {
		sharingRef.current?.announce(isActiveHere);
	}, [isActiveHere]);

	React.useEffect(() => {
		setCaptureInProgress(isActiveHere || isActiveElsewhere);
	}, [isActiveHere, isActiveElsewhere]);

	React.useEffect(() => {
		return () => {
			setCaptureInProgress(false);
		};
	}, []);

	return null;
};

export default CaptureActivityHost;
